import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import React from 'react';
import tw from 'tailwind.macro';
import Button from '../components/button';
import Layout from '../components/layout';
import LeadText from '../components/leadText';
import Section from '../components/section';
import SEO from '../components/seo';
import PageNavigator from '../components/pageNavigator';
import SafeLink from '../components/safeLink';
import Grid from '../components/grid';

const Conference2025 = ({ data, location }) => {
  const parentTitle = 'The Centre for Social Justice presents:';
  const pageTitle = 'The 6th International Social Justice Conference';

  return (
    <Layout location={location} parentTitle={parentTitle} pageTitle={pageTitle}>
      <SEO title={pageTitle} image={data.hero.childImageSharp.fluid.src} />
      <div css={[tw`relative flex items-center py-10`, `min-height:30vh;`]}>
        <div css={tw`absolute inset-0 z-0`}>
          <video
            autoPlay
            muted
            loop
            playsInline
            css={tw`w-full h-full object-cover`}
          >
            <source src="/adobestock_1478554150-2.webm" type="video/webm" />
            <source src="/adobestock_1478554150-2.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="relative container">
          <div>
            <LeadText
              css={[
                tw`my-0 uppercase tracking-wide text-white`,
                `text-shadow:0 0 10px rgba(0,0,0,0.5)`,
              ]}
              className="text-shadow"
            >
              {parentTitle}
            </LeadText>
            <h1
              className="text-shadow"
              css={[
                tw`my-0 text-white tracking-wider`,
                `text-shadow:0 0 10px rgba(0,0,0,0.5)`,
              ]}
            >
              {pageTitle}
            </h1>
          </div>
        </div>
      </div>
      <PageNavigator
        items={{
          welcome: 'Welcome',
          concept: 'Concept',
          abstracts: 'Abstracts',
          programme: 'Programme',
          previous: 'Previous Conferences',
          contact: 'Contact',
        }}
        buttonText="Join live"
        buttonLink="https://teams.microsoft.com/dl/launcher/launcher.html?url=%2F_%23%2Fl%2Fmeetup-join%2F19%3Ameeting_NWRkYjY3ZWMtMDMwOS00N2UyLTg5MzQtNzk5NWIzNTdlM2Uz%40thread.v2%2F0%3Fcontext%3D%257b%2522Tid%2522%253a%2522a6fa3b03-0a3c-4258-8433-a120dffcd348%2522%252c%2522Oid%2522%253a%252244a4c93a-11e1-4e8e-8dc9-744b72e1701f%2522%257d%26anon%3Dtrue&type=meetup-join&deeplinkId=d74d6500-842d-4ee8-9ab6-33f3b372608d&directDl=true&msLaunch=true&enableMobilePage=true"
      />
      <Section id="welcome">
        <Img
          fluid={data.Conference2025Banner.childImageSharp.fluid}
          alt="The 6th International Social Justice Conference, 15-16 October 2025"
          css={tw`mb-10 lg:mb-20 w-full`}
        />
        <Grid lgGridCols={2} lgGap={5}>
          <div>
            <h2>Welcome!</h2>
            <h3>
              The theme of the conference is{' '}
              <em>
                Social Justice, Food Security and Peace in a Turbulent World:
                Advancing Food Justice and Climate Resilience in pursuit of
                Equality, Solidarity and Sustainable Development.
              </em>
            </h3>
            <p>
              Hosted at the Cape Town International Convention Centre (CTICC),
              Cape Town, South Africa &middot; 15-16 October 2025
            </p>           
          </div>
          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/VOat_lsIz6s?si=z1XRzPV376YtJVFP"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowfullscreen
          />
        </Grid>
      </Section>
      <Section id="concept" isShaded>
        <h2>Concept</h2>
        <p>
          The 6th International Social Justice Conference (ISJC), themed{' '}
          <em>
            Social Justice, Food Security and Peace in a Turbulent World:
            Advancing Food Justice and Climate Resilience in pursuit of
            Equality, Solidarity and Sustainable Development,
          </em>
          builds on the legacy of five prior conferences that have shaped global
          discourse on social justice. Previous ISJCs have convened scholars,
          legal practitioners, policymakers and civil society to address
          systemic inequalities, producing actionable frameworks for human
          rights, constitutional protections and policy reform. Insights from
          past conferences, including robust discussions on the right to food,
          have underscored the interconnectedness of inequality, poverty and
          hunger as social justice imperatives. These efforts have informed
          regional and global advocacy, including the Global Alliance Against
          Poverty and Hunger—an initiative launched in 2024 under Brazil’s G20
          Presidency, as a flagship initiative to advance sustainable
          development goals (SDGs) 1 (No Poverty) and 2 (Zero Hunger).
        </p>
        <p>
          In 2025, the ISJC seeks to leverage the final lap of the 2030 Agenda
          for Sustainable Development, South Africa’s G20 Presidency under the
          theme
          <em>Fostering Solidarity, Equality and Sustainability,</em> and the
          Copenhagen Declaration review, to reflect on the critical nexus of
          hunger, poverty, equality, solidarity, and sustainability. The ISJC
          will deepen these discussions by fostering transformative dialogue
          among academics, legal professionals, judges, policymakers and civil
          society researchers. Hosted in the vibrant city of Cape Town at the
          Cape Town International Convention Centre from 15-16 October 2025, the
          two-day hybrid event—held in partnership with United Nations South
          Africa—will facilitate global participation, both in-person and
          online, to advance interdisciplinary solutions for a socially just
          world.
        </p>
        <Button
          to="/downloads/events/2025-10-conference-programme.pdf"
          size="md"
          css={tw`mb-2 mr-2 bg-dark-gray`}
        >
          Download programme
        </Button>
      </Section>
      <Section id="abstracts">
        <h2>Abstracts</h2>
        <p>          
          The accepted abstracts engage with the conference’s core themes by
          examining the intersections of social justice with food security,
          equality, solidarity, sustainable development and climate justice.
          They will contribute to advancing these priorities in today’s turbulent global context.
        </p>        
        <Button
          to="/downloads/events/2025-10-conference-abstracts.pdf"
          size="md"
        >
          Download abstracts
        </Button>
      </Section>
      {/* <Section id="registration" isShaded>
        <h2>Registration</h2>
        <p>You can now buy tickets to attend the conference.</p>
        <Button
          to="https://www.quicket.co.za/events/269646-5th-international-social-justice-conference-and-6th-annual-social-justice-summit#/"
          size="lg"
          css={tw`mb-2 mr-2`}
        >
          Buy tickets
        </Button>
      </Section> */}
      {/* <Section id="resources" isShaded>
        <h2>Presenter resources</h2>
        <p>The call for abstracts is now closed. Please note the following important dates:</p>
        <ul>
          <li>Deadline for submission of PowerPoint Presentations to fanele@sun.ac.za: 09 October 2024</li>
          <li>Deadline for submission of the draft paper: 11 October 2024</li>
          <li>Deadline for submission of the final paper: 15 November 2024</li>
          <li>Deadline for review and approval of the final paper by the Organising Committee: 30 November 2024</li>
        </ul>
        <br/>
        <Button
          to="/downloads/events/2024-07-SocialJustice_InternationalConference&Summit_Paper&PresentationGuidelines_03July2024versionF.pdf"
          size="md"
          css={tw`mb-2 mr-2`}
        >
          Download guidelines
        </Button> <Button
          to="/downloads/events/conference-summit-2024-poster-tips-20240724.pdf"
          size="md"
          css={tw`mb-2 mr-2 bg-dark-gray`}
        >
          Download poster tips
        </Button> <Button
          to="/downloads/events/conference-summit-2024-ppt-template.pptx"
          size="md"
          css={tw`mb-2 mr-2 bg-dark-gray`}
        >
          Download presentation template
        </Button>  
      </Section>      
      <Section id="statements" isShaded>
        <h2>Summit and conference outputs</h2>        
        <ul>          
        <li><SafeLink to="/downloads/events/2024-10-cape-town-declaration.pdf" css={tw`border-b-2 border-maroon hover:text-maroon`}>Download the Cape Town Declaration on The Role of Business and Civil Society in Advancing
Social Justice</SafeLink>, adopted at the 4th International Conference on Social Justice on 11 October 2024</li>
          <li><SafeLink to="/downloads/events/2024-10-cape-town-statement.pdf" css={tw`border-b-2 border-maroon hover:text-maroon`}>Download the Cape Town Statement on The Role of Business and Civil Society in Advancing
Social Justice</SafeLink>, adopted at the 5th Social Justice Summit on 12 October 2024</li>
        </ul>
      </Section> */}
      <Section id="programme" isShaded>
        <h2>Programme</h2>
        <LeadText>
          Download the full conference programme booklet below.
        </LeadText>
        <Button
          to="/downloads/events/2025-10-conference-programme.pdf"
          size="md"
        >
          Download programme
        </Button>
      </Section>
      <Section id="previous">
        <h2>Previous Conferences</h2>
        <LeadText>
          You can watch free recordings of previous summits and conferences on
          our website.
          <ul>
            <li>
              <SafeLink
                to="/summit-2020/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2020 summit &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/summit-conference-2021/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2021 summit and conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/summit-conference-2022/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2022 summit and conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/conference-summit-2023/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2023 summit and conference &rarr;
              </SafeLink>
            </li>
            <li>
              <SafeLink
                to="/conference-2024/"
                css={tw`border-b-2 border-maroon hover:text-maroon`}
              >
                Watch the 2024 conference &rarr;
              </SafeLink>
            </li>
          </ul>
        </LeadText>
      </Section>
      <Section id="contact" isShaded>
        <h2>Contact</h2>
        <LeadText>
          Please contact the conference project manager, Dr Marna Lourens, with
          any queries. You can reach her at{' '}
          <SafeLink
            to="mailto:mlourens@sun.ac.za"
            css={tw`border-b-2 border-maroon hover:text-maroon`}
          >
            mlourens@sun.ac.za
          </SafeLink>{' '}
          or via the contact form below.
        </LeadText>
        <form action="https://usebasin.com/f/44697ef058ef" method="POST">
          <label className="block mb-5">
            <span className="text-dark">Name:</span>
            <input
              className="form-input mt-1 block w-full"
              name="name"
              placeholder="John Smith"
            />
          </label>
          <label className="block mb-5">
            <span className="text-dark">Email:</span>
            <input
              className="form-input mt-1 block w-full"
              name="email"
              placeholder="john@smith.com"
            />
          </label>
          <label htmlFor="phone" css={tw`mb-5`}>
            <span css={tw`block`}>Phone:</span>
            <input
              name="phone"
              type="text"
              placeholder="+27 72 123 4567"
              className="form-input mt-1 block w-full"
            />
          </label>
          <label className="block mb-5">
            <span className="text-dark">Message:</span>
            <textarea
              className="form-textarea mt-1 block w-full"
              rows="3"
              name="message"
              placeholder="Your message"
            />
          </label>
          {/* <label class="flex items-center mb-5">
                  <input
                    type="checkbox"
                    name="privacy-policy-agreed"
                    class="form-checkbox"
                  />
                  <span class="ml-2">
                    I agree to the{" "}
                    <SafeLink
                      to="/privacy-policy/"
                      css={tw`border-b-2 border-primary hover:text-primary`}
                      forceBlank
                    >
                      privacy policy
                    </SafeLink>
                  </span>
  </label> */}
          <Button type="submit" size="md">
            Submit &rarr;
          </Button>
        </form>
      </Section>
    </Layout>
  );
};

export default Conference2025;

export const pageQuery = graphql`
  query {
    hero: file(relativePath: { eq: "2025-08-conference-banner.jpg" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    Conference2025Banner: file(
      relativePath: { eq: "2025-08-conference-banner.jpg" }
    ) {
      childImageSharp {
        fluid(quality: 100) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`;
